import React, { useState, useRef, useEffect } from 'react';
import { Send, Loader2, User, RotateCcw, Copy, Check, AlertCircle, Globe, Search, Mic, MicOff, Volume2, VolumeX, Phone, PhoneOff } from 'lucide-react';
import { chatWithAI, getKnowledgeForAI } from '../services/api';
import { speakWithXIA } from '../services/voiceService';

export default function AIChat({ onClose, user }) {
  const userName = user?.full_name?.split(' ')[0] || '';
  const getGreeting = () => {
    if (userName) {
      return `¡Hola ${userName}! Soy XIA, ¿en qué puedo ayudarte?`;
    }
    return '¡Hola! Soy XIA, ¿en qué puedo ayudarte?';
  };
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: getGreeting()
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [webSearchEnabled, setWebSearchEnabled] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [availableVoices, setAvailableVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [conversationMode, setConversationMode] = useState(false); // Modo hablado tipo ChatGPT
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const recognitionRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  // Cargar voces disponibles (voces femeninas prioritarias)
  useEffect(() => {
    const loadVoices = () => {
      const voices = window.speechSynthesis?.getVoices() || [];
      if (voices.length > 0) {
        setAvailableVoices(voices);
        // Buscar la mejor voz femenina en español
        // Prioridad: Microsoft Helena, Google español, cualquier voz femenina en español
        const feminineVoiceNames = ['Helena', 'Laura', 'Monica', 'Lucia', 'Conchita', 'Elvira', 'Paulina', 'Sabina'];
        const goodQualityNames = ['Microsoft', 'Google', 'Natural', 'Neural'];

        let bestVoice = null;
        let bestScore = 0;

        for (const voice of voices) {
          if (voice.lang.includes('es')) {
            let score = 1;
            // Bonus por nombre femenino
            if (feminineVoiceNames.some(n => voice.name.includes(n))) score += 10;
            // Bonus por ser de alta calidad
            if (goodQualityNames.some(n => voice.name.includes(n))) score += 5;
            // Bonus por ser local (más rápida)
            if (voice.localService) score += 2;

            if (score > bestScore) {
              bestScore = score;
              bestVoice = voice;
            }
          }
        }

        if (bestVoice) {
          setSelectedVoice(bestVoice);
          console.log('🎤 Voz seleccionada:', bestVoice.name, bestVoice.lang);
        }
      }
    };

    // Cargar voces cuando estén disponibles
    loadVoices();
    window.speechSynthesis?.addEventListener('voiceschanged', loadVoices);

    return () => {
      window.speechSynthesis?.removeEventListener('voiceschanged', loadVoices);
    };
  }, []);

  // Referencia para guardar el texto capturado
  const capturedTextRef = useRef('');

  // Inicializar grabación de audio para Whisper
  useEffect(() => {
    // Configurar MediaRecorder para grabar audio
    const setupMediaRecorder = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
        
        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            audioChunksRef.current.push(event.data);
          }
        };
        
        mediaRecorder.onstop = async () => {
          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
          audioChunksRef.current = [];
          
          // Enviar a Whisper
          try {
            const formData = new FormData();
            formData.append('audio', audioBlob, 'audio.webm');
            
            const response = await fetch('/api/voice/transcribe', {
              method: 'POST',
              body: formData
            });
            
            const result = await response.json();
            if (result.text) {
              setInput(result.text);
              capturedTextRef.current = result.text;
              
              // En modo conversación, enviar automáticamente
              if (conversationMode && result.text.trim()) {
                setTimeout(() => {
                  const form = document.querySelector('form');
                  if (form) form.requestSubmit();
                }, 300);
              }
            }
          } catch (e) {
            console.error('Error transcribiendo:', e);
            setError('Error al transcribir audio');
          }
          setIsListening(false);
        };
        
        mediaRecorderRef.current = mediaRecorder;
      } catch (e) {
        console.error('Error accediendo al micrófono:', e);
      }
    };
    
    setupMediaRecorder();
    
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      window.speechSynthesis?.cancel();
    };
  }, [conversationMode]);

  // Toggle modo conversación (como ChatGPT voice)
  const toggleConversationMode = async () => {
    if (conversationMode) {
      // Desactivar modo conversación
      setConversationMode(false);
      setVoiceEnabled(false);
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
      window.speechSynthesis?.cancel();
      setIsListening(false);
      setIsSpeaking(false);
    } else {
      // Activar modo conversación
      setConversationMode(true);
      setVoiceEnabled(true);
      // Iniciar escucha con Whisper
      startListeningWithWhisper();
    }
  };
  
  // Función para iniciar escucha con Whisper
  const startListeningWithWhisper = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      let mimeType = 'audio/webm';
      if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
        mimeType = 'audio/webm;codecs=opus';
      }
      
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());
        
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        audioChunksRef.current = [];
        setIsListening(false);
        
        if (audioBlob.size < 1000) {
          // Audio muy corto, volver a escuchar si está en modo conversación
          if (conversationMode) {
            setTimeout(() => startListeningWithWhisper(), 500);
          }
          return;
        }
        
        try {
          const formData = new FormData();
          formData.append('audio', audioBlob, 'audio.webm');
          
          const response = await fetch('/api/voice/transcribe', { method: 'POST', body: formData });
          const result = await response.json();
          
          if (result.text && result.text.trim()) {
            setInput(result.text);
            // Enviar automáticamente
            setTimeout(() => {
              const form = document.querySelector('form');
              if (form) form.requestSubmit();
            }, 200);
          } else if (conversationMode) {
            // No se detectó texto, volver a escuchar
            setTimeout(() => startListeningWithWhisper(), 500);
          }
        } catch (e) {
          console.error('Error transcribiendo:', e);
          if (conversationMode) {
            setTimeout(() => startListeningWithWhisper(), 1000);
          }
        }
      };
      
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setIsListening(true);
      
    } catch (e) {
      console.error('Error accediendo al micrófono:', e);
      setError('No se puede acceder al micrófono');
      setConversationMode(false);
    }
  };

  // Detectar idioma del texto para seleccionar voz correcta
  const detectLanguage = (text) => {
    const patterns = {
      es: /\b(el|la|los|las|un|una|de|que|en|es|por|para|con|no|si|como|hola|gracias|bien)\b/gi,
      en: /\b(the|is|are|was|were|have|has|will|would|can|hello|thank|good|yes|no)\b/gi,
      fr: /\b(le|la|les|un|une|de|que|dans|est|pour|avec|bonjour|merci|bien)\b/gi,
      de: /\b(der|die|das|den|ein|eine|und|ist|nicht|hallo|danke|gut)\b/gi,
      it: /\b(il|la|gli|un|una|di|che|è|per|con|ciao|grazie|bene)\b/gi,
      pt: /\b(o|a|os|as|um|uma|de|que|em|é|olá|obrigado|bem)\b/gi,
    };

    let maxMatches = 0;
    let detectedLang = 'es';

    for (const [lang, pattern] of Object.entries(patterns)) {
      const matches = (text.match(pattern) || []).length;
      if (matches > maxMatches) {
        maxMatches = matches;
        detectedLang = lang;
      }
    }
    return detectedLang;
  };

  // Buscar mejor voz para un idioma
  const getVoiceForLanguage = (langCode) => {
    const voices = window.speechSynthesis?.getVoices() || [];
    const feminineNames = ['Helena', 'Laura', 'Monica', 'Lucia', 'Conchita', 'Elvira', 'Zira', 'Hazel', 'Susan', 'Catherine', 'Amelie', 'Julie', 'Anna', 'Katja', 'Alice', 'Francisca'];
    const qualityNames = ['Microsoft', 'Google', 'Natural', 'Neural', 'Premium'];

    let bestVoice = null;
    let bestScore = 0;

    const langMap = {
      es: ['es-ES', 'es-MX', 'es-'],
      en: ['en-US', 'en-GB', 'en-'],
      fr: ['fr-FR', 'fr-CA', 'fr-'],
      de: ['de-DE', 'de-'],
      it: ['it-IT', 'it-'],
      pt: ['pt-BR', 'pt-PT', 'pt-']
    };

    const langPatterns = langMap[langCode] || [`${langCode}-`];

    for (const voice of voices) {
      if (langPatterns.some(p => voice.lang.includes(p))) {
        let score = 1;
        if (feminineNames.some(n => voice.name.includes(n))) score += 10;
        if (qualityNames.some(n => voice.name.includes(n))) score += 5;
        if (voice.localService) score += 2;

        if (score > bestScore) {
          bestScore = score;
          bestVoice = voice;
        }
      }
    }

    return bestVoice;
  };

  // Función para hablar - voz femenina natural
  const speakText = (text) => {
    if (!voiceEnabled || !('speechSynthesis' in window)) return;

    // Cancelar cualquier audio previo
    window.speechSynthesis.cancel();

    // Limpiar texto para mejor pronunciación
    let cleanText = text
      .replace(/```[\s\S]*?```/g, ' código omitido ') // Saltar bloques de código
      .replace(/`[^`]+`/g, ' código ') // Saltar código inline
      .replace(/\*\*([^*]+)\*\*/g, '$1') // Remover negritas
      .replace(/\*([^*]+)\*/g, '$1') // Remover itálicas
      .replace(/#{1,6}\s*/g, '') // Remover headers markdown
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Links: mostrar solo texto
      .replace(/https?:\/\/\S+/g, ' enlace ') // URLs
      .replace(/\n{2,}/g, '. ') // Dobles saltos de línea = pausa
      .replace(/\n/g, '. ') // Saltos de línea
      .replace(/[•\-\*]\s+/g, '. ') // Bullets
      .replace(/\s+/g, ' ') // Espacios múltiples
      .trim();

    // Detectar idioma del texto
    const lang = detectLanguage(cleanText);
    const voice = getVoiceForLanguage(lang) || selectedVoice;

    // Dividir en oraciones para mejor fluidez
    const sentences = cleanText.split(/(?<=[.!?])\s+/).filter(s => s.trim().length > 0);

    let currentIndex = 0;

    const speakNextSentence = () => {
      if (currentIndex >= sentences.length) {
        setIsSpeaking(false);
        // En modo conversación, reactivar micrófono
        if (conversationMode && recognitionRef.current) {
          setTimeout(() => {
            try {
              recognitionRef.current.start();
              setIsListening(true);
            } catch (e) {
              console.error('Error reactivando micro:', e);
            }
          }, 500);
        }
        return;
      }

      const sentence = sentences[currentIndex];
      const utterance = new SpeechSynthesisUtterance(sentence);

      // Configurar voz femenina y natural
      if (voice) {
        utterance.voice = voice;
        utterance.lang = voice.lang;
      } else {
        utterance.lang = lang === 'es' ? 'es-ES' : `${lang}-${lang.toUpperCase()}`;
      }

      // Parámetros para voz más natural
      utterance.rate = 0.95; // Ligeramente más lento para naturalidad
      utterance.pitch = 1.1; // Ligeramente más agudo (femenino)
      utterance.volume = 1;

      utterance.onstart = () => setIsSpeaking(true);
      utterance.onend = () => {
        currentIndex++;
        // Pequeña pausa entre oraciones para naturalidad
        setTimeout(speakNextSentence, 150);
      };
      utterance.onerror = (e) => {
        console.error('Error TTS:', e);
        setIsSpeaking(false);
      };

      window.speechSynthesis.speak(utterance);
    };

    speakNextSentence();
  };
// Toggle micrófono (Whisper)
  const toggleListening = async () => {
    // Si ya está grabando, parar
    if (isListening && mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      return;
    }
    
    // Iniciar grabación
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Detectar mejor formato soportado
      let mimeType = 'audio/webm';
      if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
        mimeType = 'audio/webm;codecs=opus';
      } else if (MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')) {
        mimeType = 'audio/ogg;codecs=opus';
      } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
        mimeType = 'audio/mp4';
      }
      
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        // Parar el stream del micrófono
        stream.getTracks().forEach(track => track.stop());
        
        const ext = mimeType.includes('webm') ? 'webm' : mimeType.includes('ogg') ? 'ogg' : 'mp4';
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        audioChunksRef.current = [];
        
        if (audioBlob.size < 100) {
          setError('No se grabó audio');
          setIsListening(false);
          return;
        }
        
        try {
          const formData = new FormData();
          formData.append('audio', audioBlob, 'audio.' + ext);
          
          const response = await fetch('/api/voice/transcribe', { 
            method: 'POST', 
            body: formData 
          });
          
          const result = await response.json();
          
          if (result.error) {
            setError(result.error);
          } else if (result.text) {
            setInput(result.text);
            if (conversationMode && result.text.trim()) {
              setTimeout(() => {
                const form = document.querySelector('form');
                if (form) form.requestSubmit();
              }, 300);
            }
          }
        } catch (e) {
          console.error('Error transcribiendo:', e);
          setError('Error al transcribir');
        }
        setIsListening(false);
      };
      
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setIsListening(true);
      
    } catch (e) {
      console.error('Error micrófono:', e);
      setError('No se puede acceder al micrófono. Verifica los permisos.');
    }
  };
  // Parar de hablar
  const stopSpeaking = () => {
    window.speechSynthesis?.cancel();
    setIsSpeaking(false);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 200) + 'px';
    }
  }, [input]);

  // Mantener focus en textarea siempre (como Claude Code)
  useEffect(() => {
    // Focus inicial al montar
    if (textareaRef.current) {
      textareaRef.current.focus();
    }

    // Recuperar focus cuando el usuario hace click en cualquier parte del chat
    const handleClick = (e) => {
      // No recuperar focus si el click fue en un botón o link
      if (e.target.tagName === 'BUTTON' || e.target.tagName === 'A' || e.target.closest('button') || e.target.closest('a')) {
        return;
      }
      // Recuperar focus con pequeño delay
      setTimeout(() => {
        if (textareaRef.current && !loading) {
          textareaRef.current.focus();
        }
      }, 10);
    };

    // También recuperar focus cuando la ventana gana el foco
    const handleWindowFocus = () => {
      setTimeout(() => {
        if (textareaRef.current && !loading) {
          textareaRef.current.focus();
        }
      }, 100);
    };

    document.addEventListener('click', handleClick);
    window.addEventListener('focus', handleWindowFocus);

    return () => {
      document.removeEventListener('click', handleClick);
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [loading]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    const wasWebSearchEnabled = webSearchEnabled;
    setInput('');
    setError('');
    setWebSearchEnabled(false); // Reset after sending

    // Si web search está activado, añadir indicador al mensaje
    const displayMessage = wasWebSearchEnabled
      ? `🌐 ${userMessage}`
      : userMessage;

    const newMessages = [...messages, { role: 'user', content: displayMessage, webSearch: wasWebSearchEnabled }];
    setMessages(newMessages);

    setLoading(true);

    try {
      let knowledgeContext = '';
      try {
        const knowledgeResponse = await getKnowledgeForAI('global');
        if (knowledgeResponse.data.context) {
          knowledgeContext = knowledgeResponse.data.context;
        }
      } catch (err) {
        console.log('No se pudo cargar conocimiento:', err);
      }

      const conversationHistory = newMessages.slice(1).map(m => ({
        role: m.role,
        content: m.content
      }));

      // Si web search está activado, añadir instrucciones especiales
      let customContext = knowledgeContext ?
        `Base de conocimiento disponible:\n${knowledgeContext}\n\nIMPORTANTE: Usa esta informacion para responder si es relevante.` : '';

      if (wasWebSearchEnabled) {
        customContext += `\n\n🌐 BUSQUEDA WEB ACTIVADA: El usuario quiere que busques informacion en internet sobre "${userMessage}".

SITIOS WEB DE REFERENCIA PARA PRECIOS Y PRODUCTOS:
- PcComponentes (pccomponentes.com) - Tienda española de informatica
- Amazon España (amazon.es) - Marketplace general
- MediaMarkt (mediamarkt.es) - Electronica y tecnologia
- Coolmod (coolmod.com) - Componentes PC gaming
- Newegg (newegg.com) - Componentes USA
- AliExpress (aliexpress.com) - Precios economicos desde China
- Alternate (alternate.es) - Componentes europeos

SITIOS TECNICOS:
- Guru3D (guru3d.com) - Drivers y herramientas
- TechPowerUp (techpowerup.com) - Reviews y drivers
- Tom's Hardware (tomshardware.com) - Reviews hardware
- AnandTech - Benchmarks detallados
- GamersNexus - Reviews tecnicos

Proporciona enlaces directos cuando sea posible. Indica precios aproximados si los conoces.
Si no tienes informacion actualizada de precios, indica que el usuario deberia verificar los precios actuales en las webs mencionadas.`;
      }

      // Llamar a XIA MEJORADA con búsqueda web real
      const response = await chatWithAI(userMessage, conversationHistory, customContext, wasWebSearchEnabled);

      const assistantResponse = response.data.response;
      const webResults = response.data.webResults;

      setMessages([
        ...newMessages,
        {
          role: 'assistant',
          content: assistantResponse,
          webSearch: wasWebSearchEnabled,
          webResults: webResults,
          provider: response.data.provider,
          elapsed: response.data.elapsed
        }
      ]);

      // Leer respuesta en voz alta si está activado
      if (voiceEnabled) {
        speakWithXIA(assistantResponse, () => setIsSpeaking(true), () => { setIsSpeaking(false); if (conversationMode) { setTimeout(() => startListeningWithWhisper(), 500); } }).catch(() => speakText(assistantResponse));
      }
    } catch (err) {
      console.error('Error AI chat:', err);
      const errorMsg = err.response?.data?.error || 'Error al comunicarse con la IA';
      const errorDetails = err.response?.data?.details || '';
      setError(errorDetails ? `${errorMsg}: ${errorDetails}` : errorMsg);
      setMessages(messages);
    } finally {
      setLoading(false);
    }
  };

  // Recuperar foco cuando loading cambia a false
  useEffect(() => {
    if (!loading && textareaRef.current) {
      // Usar requestAnimationFrame para asegurar que el DOM está actualizado
      requestAnimationFrame(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
        }
      });
    }
  }, [loading]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const copyToClipboard = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const resetChat = () => {
    setMessages([
      {
        role: 'assistant',
        content: getGreeting()
      }
    ]);
    setError('');
  };

  return (
    <div className="flex flex-col h-full bg-white rounded-xl overflow-hidden" style={{ fontFamily: "'Söhne', 'ui-sans-serif', 'system-ui', '-apple-system', 'Segoe UI', Roboto, Ubuntu, Cantarell, 'Noto Sans', sans-serif" }}>
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center">
          <img src="/xia-icon.png" alt="XIA" className="w-10 h-10 mr-3 object-contain" />
          <div>
            <h3 className="font-medium text-gray-900 text-[15px]">XIA</h3>
            <p className="text-xs text-gray-500">Asistente de Manantial</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Botón Modo Conversación - Estilo ChatGPT */}
          <button
            onClick={toggleConversationMode}
            className={`relative flex items-center gap-2 px-4 py-2 rounded-full transition-all duration-300 font-medium text-sm ${
              conversationMode
                ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-lg shadow-green-500/50'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-md'
            }`}
            title={conversationMode ? 'Terminar conversación' : 'Modo conversación por voz'}
            style={conversationMode ? { animation: 'pulse-glow 2s ease-in-out infinite' } : {}}
          >
            {/* Indicador de audio activo */}
            {conversationMode && isListening && (
              <div className="absolute -left-1 -top-1 w-3 h-3">
                <span className="absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75 animate-ping"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
              </div>
            )}

            {/* Icono */}
            {conversationMode ? (
              <div className="flex items-center gap-1">
                {isListening ? (
                  <div className="flex gap-0.5">
                    <span className="w-1 h-3 bg-white rounded-full animate-pulse"></span>
                    <span className="w-1 h-4 bg-white rounded-full animate-pulse" style={{animationDelay: '0.1s'}}></span>
                    <span className="w-1 h-2 bg-white rounded-full animate-pulse" style={{animationDelay: '0.2s'}}></span>
                  </div>
                ) : isSpeaking ? (
                  <Volume2 className="w-4 h-4 animate-pulse" />
                ) : (
                  <PhoneOff className="w-4 h-4" />
                )}
              </div>
            ) : (
              <Mic className="w-4 h-4" />
            )}

            {/* Texto */}
            <span className="hidden sm:inline">
              {conversationMode
                ? (isListening ? 'Escuchando...' : isSpeaking ? 'Hablando...' : 'Terminar')
                : 'Voz'
              }
            </span>
          </button>

          {/* Botón Reset */}
          <button
            onClick={resetChat}
            className="p-2 hover:bg-gray-100 rounded-lg transition text-gray-500 hover:text-gray-900"
            title="Nueva conversacion"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        </div>

        {/* CSS para animación de brillo */}
        <style>{`
          @keyframes pulse-glow {
            0%, 100% { box-shadow: 0 0 5px rgba(34, 197, 94, 0.5), 0 0 20px rgba(34, 197, 94, 0.3); }
            50% { box-shadow: 0 0 20px rgba(34, 197, 94, 0.8), 0 0 40px rgba(34, 197, 94, 0.4); }
          }
        `}</style>
      </div>

      {/* Messages - Estilo WhatsApp */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-white">
        {messages.map((message, index) => (
          <div
            key={index}
            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[75%] px-3 py-2 rounded-2xl shadow-sm ${
                message.role === 'user'
                  ? 'bg-[#60a5fa] text-white rounded-tr-sm'
                  : 'bg-gray-100 text-gray-800 rounded-tl-sm'
              }`}
            >
              <div className="text-[15px] whitespace-pre-wrap">
                {message.content}
              </div>
              <div className={`text-[11px] mt-1 ${message.role === 'user' ? 'text-right text-blue-100' : 'text-gray-400'}`}>
                {new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
          </div>
        ))}

        {/* Loading */}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-gray-100 px-4 py-3 rounded-2xl rounded-tl-sm shadow-sm">
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
              </div>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="flex justify-start">
            <div className="bg-red-100 px-3 py-2 rounded-2xl rounded-tl-sm shadow-sm text-red-600 text-sm">
              {error}
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-4 bg-white border-t border-gray-200">
        <form onSubmit={handleSubmit} className="max-w-3xl mx-auto">
          {/* Indicador de búsqueda web activa */}
          {webSearchEnabled && (
            <div className="flex items-center gap-2 mb-2 px-2 py-1 bg-blue-50 rounded-lg text-blue-600 text-sm">
              <Globe className="w-4 h-4" />
              <span>Búsqueda web activada - XIA buscará información en internet</span>
              <button
                type="button"
                onClick={() => setWebSearchEnabled(false)}
                className="ml-auto text-blue-500 hover:text-blue-700"
              >
                ✕
              </button>
            </div>
          )}

          <div className="relative flex items-end bg-white rounded-xl border border-gray-300 shadow-sm">
            {/* Botón de búsqueda web */}
            <button
              type="button"
              onClick={() => setWebSearchEnabled(!webSearchEnabled)}
              className={`p-2 m-1 rounded-lg transition ${
                webSearchEnabled
                  ? 'bg-blue-100 text-blue-600'
                  : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
              }`}
              title={webSearchEnabled ? 'Web activada' : 'Buscar en web'}
            >
              <Globe className="w-5 h-5" />
            </button>

            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={(e) => {
                // Permitir paste explícitamente con Ctrl+V
                const pastedText = e.clipboardData.getData('text');
                if (pastedText) {
                  e.preventDefault();
                  const start = e.target.selectionStart;
                  const end = e.target.selectionEnd;
                  const newText = input.substring(0, start) + pastedText + input.substring(end);
                  setInput(newText);
                  setTimeout(() => {
                    e.target.selectionStart = e.target.selectionEnd = start + pastedText.length;
                  }, 0);
                }
              }}
              placeholder={isListening ? '🎤 Escuchando...' : (webSearchEnabled ? "Buscar en la web: ej. 'precio RTX 4070'" : "Envia un mensaje o usa el micrófono...")}
              rows={1}
              className="flex-1 px-4 py-3 bg-transparent text-gray-800 text-[15px] placeholder-gray-400 resize-none focus:outline-none"
              style={{ maxHeight: '200px' }}
              disabled={loading || isListening}
            />
{/* Botón Micrófono */}            <button              type="button"              onClick={toggleListening}              disabled={loading}              className={`p-2 m-1 rounded-lg transition ${isListening ? "bg-red-100 text-red-600 animate-pulse" : "text-gray-400 hover:bg-gray-100 hover:text-gray-600"}`}              title={isListening ? "Detener" : "Hablar"}            >              {isListening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}            </button>            {/* Botón Altavoz */}            <button              type="button"              onClick={() => setVoiceEnabled(!voiceEnabled)}              className={`p-2 m-1 rounded-lg transition ${voiceEnabled ? "bg-green-100 text-green-600" : "text-gray-400 hover:bg-gray-100 hover:text-gray-600"}`}              title={voiceEnabled ? "Voz activada" : "Activar voz"}            >              {voiceEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}            </button>
            {/* Botón Enviar */}
            <button
              type="submit"
              disabled={!input.trim() || loading}
              className="p-2 m-1 rounded-lg transition disabled:opacity-30 disabled:cursor-not-allowed bg-[#3B82F6] hover:bg-blue-600 text-white"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
          <p className="text-center text-xs text-gray-500 mt-2">
            {webSearchEnabled ? (
              <span className="text-blue-600">🌐 Modo búsqueda web - Pregunta sobre precios, productos o información técnica</span>
            ) : (
              'XIA puede cometer errores. Verifica la informacion importante.'
            )}
          </p>
        </form>
      </div>
    </div>
  );
}
